import React, { useState, useEffect, useCallback } from 'react';
import { BarChartIcon } from '../../components/Icons';
import { apiFetch } from '../../hooks/useApi';

const fmt = v => {
  if (!v && v !== 0) return '—';
  return Number(v).toLocaleString('pt-AO') + ' Kz';
};

const pct = v => (v === null || v === undefined) ? null : `${v > 0 ? '+' : ''}${v}%`;

export default function FinanceiroAdmin() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await apiFetch('/admin/financeiro');
      if (res.sucesso) setDados(res);
      else setErro(res.mensagem || 'Erro ao carregar dados');
    } catch (e) {
      setErro('Erro ao ligar ao servidor');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const exportarRelatorio = () => {
    if (!dados) return;
    const { kpis, mensais, distribuicao } = dados;
    const linhas = [
      ['Métrica', 'Valor'],
      ['GMV acumulado', fmt(kpis.gmv_total)],
      ['Receita total', fmt(kpis.receita_total)],
      ['GMV este mês', fmt(kpis.gmv_mes)],
      ['Receita este mês', fmt(kpis.receita_mes)],
      ['Ticket médio', fmt(kpis.ticket_medio)],
      ['Taxa de cancelamento', kpis.taxa_cancelamento + '%'],
      ['Reservas concluídas', kpis.reservas_concluidas],
      [''],
      ['Distribuição receita'],
      ['Comissão aluguer', fmt(distribuicao.comissao_aluguer), distribuicao.pct_comissao + '%'],
      ['Taxa logística', fmt(distribuicao.taxa_entrega), distribuicao.pct_entrega + '%'],
      [''],
      ['Mês', 'GMV (Kz)', 'Receita (Kz)', 'Transacções'],
      ...mensais.map(m => [m.mes, m.gmv, m.receita, m.transacoes]),
    ];
    const csv  = linhas.map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'relatorio_financeiro_kialuga.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const kpis     = dados?.kpis       || {};
  const mensais  = dados?.mensais    || [];
  const dist     = dados?.distribuicao || {};

  // Calcular altura máxima para o gráfico
  const maxGmv = Math.max(...mensais.map(m => m.gmv), 1);

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Receitas & GMV</div>
          <div className="topbar-sub">Análise financeira em Kwanzas — dados reais</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-ghost" onClick={carregar} title="Actualizar">
            ↻
          </button>
          <button className="btn btn-outline" onClick={exportarRelatorio} disabled={!dados}>
            Exportar relatório
          </button>
        </div>
      </div>

      <div className="page-content">

        {erro && (
          <div style={{background:'var(--danger-bg)',border:'1px solid var(--danger-border)',borderRadius:'var(--radius-lg)',padding:'12px 16px',color:'var(--danger)',marginBottom:'16px',fontSize:'13px'}}>
            {erro}
          </div>
        )}

        {carregando ? (
          <div style={{textAlign:'center',padding:'60px',color:'var(--text-muted)',fontSize:'13px'}}>A carregar dados financeiros…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="stats-grid stats-grid-5">
              <div className="stat-card dark">
                <div className="stat-label">GMV acumulado</div>
                <div className="stat-value" style={{fontSize:'20px'}}>{fmt(kpis.gmv_total)}</div>
                <div className="stat-change change-up">↑ Desde o início</div>
              </div>
              <div className="stat-card orange">
                <div className="stat-label">Receita total</div>
                <div className="stat-value" style={{fontSize:'20px'}}>{fmt(kpis.receita_total)}</div>
                <div className="stat-change">Take rate 10%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">GMV este mês</div>
                <div className="stat-value" style={{fontSize:'20px'}}>{fmt(kpis.gmv_mes)}</div>
                {kpis.variacao_gmv_mes !== null && kpis.variacao_gmv_mes !== undefined ? (
                  <div className={`stat-change ${kpis.variacao_gmv_mes >= 0 ? 'change-up' : 'change-down'}`}>
                    {kpis.variacao_gmv_mes >= 0 ? '↑' : '↓'} {Math.abs(kpis.variacao_gmv_mes)}% vs mês ant.
                  </div>
                ) : (
                  <div className="stat-change">Primeiro mês</div>
                )}
              </div>
              <div className="stat-card">
                <div className="stat-label">Receita este mês</div>
                <div className="stat-value" style={{fontSize:'20px'}}>{fmt(kpis.receita_mes)}</div>
                {kpis.variacao_rec_mes !== null && kpis.variacao_rec_mes !== undefined ? (
                  <div className={`stat-change ${kpis.variacao_rec_mes >= 0 ? 'change-up' : 'change-down'}`}>
                    {kpis.variacao_rec_mes >= 0 ? '↑' : '↓'} {Math.abs(kpis.variacao_rec_mes)}% vs mês ant.
                  </div>
                ) : (
                  <div className="stat-change">Primeiro mês</div>
                )}
              </div>
              <div className="stat-card">
                <div className="stat-label">Reservas concluídas</div>
                <div className="stat-value" style={{fontSize:'26px'}}>{kpis.reservas_concluidas ?? 0}</div>
                <div className="stat-change">Cancel. {kpis.taxa_cancelamento ?? 0}%</div>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:'14px'}}>
              {/* Gráfico mensal */}
              <div className="card">
                <div style={{fontSize:'14px',fontWeight:'600',marginBottom:'14px'}}>GMV mensal vs Receita (Kz)</div>
                {mensais.length === 0 ? (
                  <div style={{textAlign:'center',padding:'40px',color:'var(--text-muted)',fontSize:'13px'}}>
                    Sem dados suficientes para o gráfico
                  </div>
                ) : (
                  <>
                    <div style={{display:'flex',alignItems:'flex-end',gap:'8px',height:'140px',padding:'0 4px 4px'}}>
                      {mensais.map((m, i) => {
                        const hGmv     = Math.round((m.gmv / maxGmv) * 100);
                        const hReceita = Math.round((m.receita / maxGmv) * 100);
                        const isLast   = i === mensais.length - 1;
                        return (
                          <div key={m.mes_key} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'4px'}} title={`${m.mes}: GMV ${fmt(m.gmv)} | Receita ${fmt(m.receita)}`}>
                            <div style={{width:'100%',borderRadius:'4px 4px 0 0',background:isLast?'var(--orange)':'rgba(249,115,22,0.35)',height:`${Math.max(hGmv,2)}%`,minHeight:'2px'}}></div>
                            <div style={{width:'100%',borderRadius:'4px 4px 0 0',background:isLast?'var(--brand-mid)':'rgba(37,99,168,0.35)',height:`${Math.max(hReceita,2)}%`,minHeight:'2px',marginTop:'2px'}}></div>
                            <span style={{fontSize:'9px',color:'var(--text-muted)'}}>{m.mes}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{display:'flex',gap:'16px',marginTop:'8px',paddingLeft:'4px'}}>
                      {[['var(--orange)','GMV (Kz)'],['var(--brand-mid)','Receita KiAluga (Kz)']].map(([c,l])=>(
                        <div key={l} style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'10px',color:'var(--text-muted)'}}>
                          <div style={{width:'10px',height:'10px',borderRadius:'2px',background:c}}></div>{l}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                {/* Distribuição receita */}
                <div className="card">
                  <div style={{fontSize:'14px',fontWeight:'600',marginBottom:'12px'}}>Distribuição receita</div>
                  {dist.total > 0 ? (
                    <>
                      {[
                        ['Comissão aluguer', dist.comissao_aluguer, dist.pct_comissao, 'var(--orange)'],
                        ['Taxa logística',   dist.taxa_entrega,     dist.pct_entrega,  'var(--success)'],
                      ].map(([l,v,w,c])=>(
                        <div key={l} style={{marginBottom:'10px'}}>
                          <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',marginBottom:'4px'}}>
                            <span style={{color:'var(--text-secondary)'}}>{l}</span>
                            <span style={{fontWeight:'600'}}>{fmt(v)} ({w}%)</span>
                          </div>
                          <div style={{height:'5px',background:'var(--surface)',borderRadius:'3px',overflow:'hidden'}}>
                            <div style={{width:`${w}%`,height:'100%',background:c,borderRadius:'3px'}}></div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{fontSize:'12px',color:'var(--text-muted)',textAlign:'center',padding:'12px 0'}}>
                      Sem transacções ainda
                    </div>
                  )}
                </div>

                {/* Ticket médio */}
                <div style={{background:'var(--info-bg)',border:'1px solid var(--info-border)',borderRadius:'var(--radius-lg)',padding:'14px 16px',fontSize:'12px',color:'var(--info)'}}>
                  <div style={{fontWeight:'600',marginBottom:'4px',display:'flex',alignItems:'center',gap:'6px'}}>
                    <BarChartIcon size={14} color='var(--info)'/> Ticket médio por transacção
                  </div>
                  <div style={{fontSize:'20px',fontWeight:'700',marginBottom:'2px',color:'var(--text-primary)'}}>
                    {fmt(kpis.ticket_medio)}
                  </div>
                  <div style={{fontSize:'11px',color:'var(--text-secondary)'}}>
                    Baseado em {kpis.reservas_concluidas ?? 0} reserva(s) concluída(s)
                  </div>
                </div>

                {/* Tabela resumo últimos meses */}
                {mensais.length > 0 && (
                  <div className="card" style={{padding:'12px'}}>
                    <div style={{fontSize:'12px',fontWeight:'600',marginBottom:'8px'}}>Resumo mensal</div>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                      <thead>
                        <tr style={{color:'var(--text-muted)'}}>
                          <th style={{textAlign:'left',paddingBottom:'4px'}}>Mês</th>
                          <th style={{textAlign:'right',paddingBottom:'4px'}}>GMV</th>
                          <th style={{textAlign:'right',paddingBottom:'4px'}}>Receita</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mensais.slice(-5).map(m => (
                          <tr key={m.mes_key} style={{borderTop:'1px solid var(--border)'}}>
                            <td style={{padding:'3px 0',color:'var(--text-secondary)'}}>{m.mes}</td>
                            <td style={{textAlign:'right',fontWeight:'500'}}>{Number(m.gmv).toLocaleString('pt-AO')}</td>
                            <td style={{textAlign:'right',color:'var(--orange)',fontWeight:'600'}}>{Number(m.receita).toLocaleString('pt-AO')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
